import { Link, Outlet, useNavigate } from '@tanstack/react-router'
import { Menu, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { TenantResponse } from '@/api/responses'
import { Spinner } from '@/components/ui'
import { useSession } from '@/features/auth/session'
import { BillingBanner } from '@/features/billing/components/BillingBanner'
import { CreditsIndicator } from '@/features/billing/components/CreditsIndicator'
import { PresenceControl } from '@/features/presence/PresenceControl'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'
import { cn } from '@/lib/utils'

import { TenantSwitcher } from './TenantSwitcher'
import { useDisclosure } from './useDisclosure'
import { UserMenu } from './UserMenu'
import { type NavSectionItem, useNavSections } from './useNavSections'

function NavList({
  items,
  tenantSlug,
  collapsed,
  onNavigate,
}: {
  readonly items: readonly NavSectionItem[]
  readonly tenantSlug: string
  readonly collapsed: boolean
  readonly onNavigate: () => void
}) {
  return (
    <ul className="flex flex-col gap-1">
      {items.map((item) => (
        <li key={item.key}>
          <Link
            to={item.to}
            params={{ tenantSlug }}
            onClick={onNavigate}
            className="relative flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium text-foreground-secondary hover:bg-background-elevated hover:text-foreground-primary [&.active]:bg-background-elevated [&.active]:text-foreground-primary"
            activeProps={{ className: 'active' }}
          >
            <item.icon aria-hidden="true" className="h-4 w-4 shrink-0" />
            {!collapsed ? <span className="truncate">{item.label}</span> : null}
            {item.badge !== undefined && item.badge > 0 ? (
              <span
                aria-label={`${item.label}: ${String(item.badge)}`}
                className={cn(
                  'rounded-full bg-action-primary px-1.5 text-[10px] font-semibold leading-4 text-foreground-on-action',
                  collapsed ? 'absolute right-1 top-1' : 'ml-auto',
                )}
              >
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  )
}

/**
 * Shell do produto autenticado: em telas estreitas (< md / 768px) a sidebar vira um drawer
 * off-canvas (escondida por padrao, aberta pelo botao de menu na topbar mobile, fecha ao
 * navegar, ao tocar fora ou com Escape); em telas >= md permanece fixa no layout, com o toggle
 * de recolher/expandir de sempre. `position: fixed` no mobile tira a sidebar do fluxo do flex,
 * entao ela nunca disputa largura com o conteudo (raiz do overflow horizontal corrigido aqui).
 */
export function AppShellLayout() {
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState(false)
  const { isOpen: mobileOpen, setIsOpen: setMobileOpen, containerRef: asideRef } = useDisclosure()
  const { user } = useSession()
  const { tenant, tenants } = useCurrentTenant()
  const navigate = useNavigate()
  const { primary, secondary } = useNavSections(tenant?.id)

  if (user === null || tenant === null) {
    return (
      <div className="flex min-h-full items-center justify-center p-8">
        <Spinner label={t('loading')} />
      </div>
    )
  }

  function handleTenantSelect(next: TenantResponse): void {
    setMobileOpen(false)
    void navigate({ to: '/app/$tenantSlug/dashboard', params: { tenantSlug: next.slug } })
  }

  function closeMobileMenu(): void {
    setMobileOpen(false)
  }

  return (
    <div className="flex min-h-full overflow-x-hidden">
      {/* Backdrop do drawer mobile: toca fora fecha o menu. */}
      {mobileOpen ? (
        <div
          aria-hidden="true"
          onClick={closeMobileMenu}
          className="fixed inset-0 z-30 bg-foreground-primary/50 md:hidden"
        />
      ) : null}
      <aside
        ref={asideRef}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col overflow-hidden border-r border-border-default bg-background-subtle transition-transform duration-200 ease-out',
          'md:static md:z-auto md:translate-x-0 md:transition-[width] md:duration-200',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'md:w-16' : 'md:w-64',
        )}
      >
        <div className="flex items-center gap-1 p-3">
          {!collapsed ? (
            <TenantSwitcher
              tenants={tenants}
              currentTenant={tenant}
              onSelect={handleTenantSelect}
            />
          ) : null}
          {/* Fecha o drawer (so existe/e' visivel no mobile). */}
          <button
            type="button"
            onClick={closeMobileMenu}
            aria-label={t('shell.sidebar.closeMenu')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-muted hover:bg-background-elevated focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring md:hidden"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
          {/* Recolhe/expande a sidebar fixa (so existe/e' visivel no desktop). */}
          <button
            type="button"
            onClick={() => {
              setCollapsed((value) => !value)
            }}
            aria-label={collapsed ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
            className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-muted hover:bg-background-elevated focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring md:flex"
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden="true" className="h-4 w-4" />
            ) : (
              <PanelLeftClose aria-hidden="true" className="h-4 w-4" />
            )}
          </button>
        </div>
        <nav className="flex flex-1 flex-col justify-between overflow-y-auto px-2 pb-3">
          <NavList
            items={primary}
            tenantSlug={tenant.slug}
            collapsed={collapsed}
            onNavigate={closeMobileMenu}
          />
          <div className="border-t border-border-default pt-2">
            <NavList
              items={secondary}
              tenantSlug={tenant.slug}
              collapsed={collapsed}
              onNavigate={closeMobileMenu}
            />
          </div>
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border-default px-4">
          {/* Abre o drawer (so existe/e' visivel no mobile). */}
          <button
            type="button"
            onClick={() => {
              setMobileOpen(true)
            }}
            aria-label={t('shell.sidebar.openMenu')}
            aria-expanded={mobileOpen}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-muted hover:bg-background-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring md:hidden"
          >
            <Menu aria-hidden="true" className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <CreditsIndicator tenantId={tenant.id} tenantSlug={tenant.slug} />
          <PresenceControl tenantId={tenant.id} tenantSlug={tenant.slug} />
          <UserMenu user={user} tenantId={tenant.id} />
        </header>
        <BillingBanner tenantId={tenant.id} tenantSlug={tenant.slug} />
        <main className="min-w-0 flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
